import classNames from 'classnames';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTypedSelector } from '../../../../store/reducers/use-typed-selector';
import { EventsState } from '../../../../store/types';
import { TemplateClassNames, TemplateNames } from '../../../../types/template-layouts';
import { EPreviewTypes, PageModuleType, Session } from '../../../../types/working-model';
import { checkIsLive } from '../../../../utils/utils';
import { TypographyItem } from '../../../general-ui/typography-item/typography-item';
import Agenda from '../../modules/agenda/agenda';
import MainEventBannerLive from '../../modules/main-event-banner-live/main-event-banner-live';
import Footer from '../footer/footer';
import MarketingNav from '../navigation/marketing-nav';
import './marketing-registered-agenda.scss';

function sortSessionsByTime(a: Session, b: Session) {
	if (a.session_type === 'on-demand') {
		return 1;
	}

	return Math.abs(a.timestamp! - b.timestamp!);
}

const MarketingRegisteredAgenda: React.FC = () => {
	const {
		LiveEventReducer: { eventBundle },
	} = useTypedSelector((event: EventsState) => ({
		LiveEventReducer: event.LiveEventReducer
	}));
	const workingEvent = useTypedSelector(state => state.CreateEventReducer.workingEvent);
	const previewMode = useTypedSelector(state => state.CreateEventReducer.previewMode);

	const isPreview = previewMode === EPreviewTypes.Agenda;

	const homepage = isPreview ? workingEvent?.homepage : eventBundle?.homepage;
	const template = (isPreview ? workingEvent?.template.name : eventBundle?.template.name) || TemplateNames.Classic;
	const sessions = isPreview ? workingEvent?.sessions : eventBundle?.sessions;
	const { t } = useTranslation("homepage");
	const logo = isPreview ? workingEvent?.homepage?.header.logo : eventBundle?.homepage?.header.logo;

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const renderAgendaModules = useMemo(() => {
		if (isPreview) {
			if (!workingEvent || !homepage) return null;
			const sortedSessions = workingEvent.sessions?.sort(sortSessionsByTime) ?? [];

			const liveSession = sortedSessions.find(checkIsLive);

			const startDate = liveSession?.timestamp ? new Date(liveSession.timestamp) : null;

			// find and copy homepage aganda module to be reused for Agenda page
			const homepageAgenda = homepage.modules.find(_m => _m.type === PageModuleType.agenda);
			let agenda = homepageAgenda && { ...homepageAgenda };

			if (agenda?.content?.layout_type) {
				if (TemplateClassNames[template] === TemplateClassNames.O2) {
					agenda = {
						...agenda,
						content: {
							...agenda.content,
							layout_type: 'vertical_card'
						}
					};
				} else {
					agenda = {
						...agenda,
						content: {
							...agenda.content,
							layout_type: 'standard'
						}
					};
				}
			}

			const mainEventBanner = homepage.modules.find(_m => _m.type === PageModuleType.main_event_banner);

			return agenda ? (
				<>
					{(liveSession && mainEventBanner && startDate) ? (
						<MainEventBannerLive
							header={homepage?.header}
							module={mainEventBanner}
							template={TemplateClassNames[template]}
							startDate={startDate}
							key={mainEventBanner.id}
							liveSession={liveSession}
						/>
					) : null
					}
					<TypographyItem className="evt-title-1 marketing-registered-agenda-title">
						{t("Agenda")}
					</TypographyItem>
					<Agenda
						isLive={true}
						module={agenda}
						template={TemplateClassNames[template]}
						sessions={sessions!}
						key={agenda.id}
						eventBundle={workingEvent}
					/>
				</>
			) : null;

		} else {
			if (!eventBundle || !homepage) return null;

			const sortedSessions = eventBundle.sessions?.sort(sortSessionsByTime) ?? [];

			const liveSession = sortedSessions.find(checkIsLive);

			const startDate = liveSession?.timestamp ? new Date(liveSession.timestamp) : null;

			// find and copy homepage aganda module to be reused for Agenda page

			const homepageAgenda = homepage.modules.find(_m => _m.type === PageModuleType.agenda);

			let agenda = homepageAgenda && { ...homepageAgenda };

			if (agenda?.content?.layout_type) {
				if (TemplateClassNames[template] === TemplateClassNames.O2) {
					agenda = {
						...agenda,
						content: {
							...agenda.content,
							layout_type: 'vertical_card'
						}
					};
				} else {
					agenda = {
						...agenda,
						content: {
							...agenda.content,
							layout_type: 'standard'
						}
					};
				}
			}

			const mainEventBanner = homepage.modules.find(_m => _m.type === PageModuleType.main_event_banner);

			return agenda ? (
				<>
					{(liveSession && mainEventBanner && startDate) ? (
						<MainEventBannerLive
							header={homepage?.header}
							module={mainEventBanner}
							template={TemplateClassNames[template]}
							startDate={startDate}
							key={mainEventBanner.id}
							liveSession={liveSession}
						/>
					) : null
					}
					<TypographyItem className="evt-title-1 marketing-registered-agenda-title">
						{t("Agenda")}
					</TypographyItem>
					<Agenda
						isLive={true}
						module={agenda}
						template={TemplateClassNames[template]}
						sessions={sessions!}
						key={agenda.id}
						eventBundle={eventBundle}
					/>
				</>
			) : null;
		}
	}, [eventBundle, homepage, sessions, t, template, isPreview, workingEvent]);

	return (
		<div className={classNames('marketing-registered-page agenda-wrapper')}>
			<MarketingNav showLanguageSelector template={TemplateClassNames[template]} logo={logo ?? undefined} />
			<div className="registered-main-contant">
				{renderAgendaModules}
				{homepage?.footer && (
					<Footer footer={homepage.footer} template={TemplateClassNames[template]} />
				)}
			</div>
		</div>
	);
};

export default MarketingRegisteredAgenda;
